import React, {useCallback, useContext, useState} from 'react';
import {ReactSortable} from 'react-sortablejs';
import TextButton from '../../ui/TextButton';
import PrimaryButton from '../../ui/PrimaryButton';
import SortableSection from './SortableSection';
import InstructionSection from './InstructionSection';
import ReactForm from '../ReactForm';
import {ConfigurationRules} from '../../models/form/ConfigurationRules';
import SectionConfiguration from '../../models/form/SectionConfiguration';
import {IFieldSkeleton} from '../../models/form/IFieldSkeleton';
import FormConfiguration, {IFormConfiguration} from '../../models/form/FormConfiguration';
import {SectionSkeleton} from '../../models/form/SectionSkeleton';
import getLanguage, {DEFAULT_LANGUAGE, LanguageId} from '../../models/utils/Languages';
import FieldConfiguration from '../../models/form/FieldConfiguration';
import SideFieldForm from './sideForm/SideFieldForm';
import SideSectionForm from './sideForm/SideSectionForm';
import Tip, {TipType} from '../../ui/Tip';
import UILink from '../../ui/UILink';
import LanguageFilter from './components/LanguageFilter';
import {SectionId} from '../../models/form/types';
import ConfigurationList from '../../models/form/ConfigurationList';
import {SectionConfigurationQL} from '../../models/graphql/SectionConfigurationQL';
import {prepareFormInput} from './logic/formLogic';
import ConfigurationRulesBlock from './rules/ConfigurationRulesBlock';
import {ActionType, ElementType, FormTypeContext} from './types';
import {EditingRights} from '../../models/form/EditingRights';
import ReadOnlyForm from './components/ReadOnlyForm';
import {Route, Switch, useHistory, useRouteMatch} from 'react-router';
import NewSidePanel from '../../ui/NewSidePanel';
import SideFormListOfFields from './sideForm/SideFormListOfFields';
import {getSectionConfiguration} from './Sections';

type FormProps = {
  sectionConfigurations: SectionConfigurationQL[]
  backLink: string
  onSubmit?: (input: any) => void
  withInstructions: boolean
  excludeV1: boolean
  tip?: React.ReactNode
  formConfiguration?: IFormConfiguration
}

export default function Form({
  formConfiguration,
  sectionConfigurations,
  backLink,
  onSubmit,
  tip,
  withInstructions,
  excludeV1,
}: FormProps) {
  const [configurations, setConfigurations] = useState<SectionConfiguration[]>(sectionConfigurations.map((x) => SectionConfiguration.fromQL(x)));
  const [sections, setSections] = useState<SectionSkeleton[]>(FormConfiguration.initialize(formConfiguration || {}, configurations));
  const [instructions, setInstructions] = useState(formConfiguration?.instructions);
  const [rules, setRules] = useState<ConfigurationRules>(formConfiguration?.rules || {
    freePaidStatus: 'FreePaid',
    defaultLanguage: DEFAULT_LANGUAGE,
    privatePublicStatus: 'PrivatePublic',
    eventTypeIds: [],
  });
  const editingRights = formConfiguration?.editingRights || EditingRights.Limited;
  const formTypeContext = useContext(FormTypeContext);

  const history = useHistory();
  const match = useRouteMatch();

  const [language, setLanguage] = useState<LanguageId>(rules.defaultLanguage);

  const deleteSection = (id: SectionId) => {
    const CUSTOM_ID = 'custom'
    const section = getSectionConfiguration(id, configurations);
    const custom = getSectionConfiguration(CUSTOM_ID, configurations);
    const list = new ConfigurationList(configurations, sections);
    const withReplaceSection = section && custom ? list.replace(CUSTOM_ID, custom.replaceFields(custom.fields.concat(section.fields))) : list;
    setConfigurations(withReplaceSection.delete(id).configurations)
  }

  const deleteElement = (type: ElementType, id: string) => {
    if (type == ElementType.Section) {
      deleteSection(id);
      setSections(sections.filter((x) => x.id !== id));
    } else {
      setConfigurations(configurations.map((x) => x.removeFields([id])));
      setSections(sections.map((x) => ({...x, fields: x.fields.filter((y) => y.id !== id)})));
    }
  };

  const finishElementCreateUpdate = (action: ActionType, el?: SectionConfiguration | FieldConfiguration) => {
    const list = new ConfigurationList(configurations, sections);
    if (action === ActionType.Created) {
      if (el instanceof SectionConfiguration) {
        const newList = list.append(el);
        setConfigurations(newList.configurations);
      } else if (el instanceof FieldConfiguration) {
        const newList = list.appendField(el);
        setConfigurations(newList.configurations);
      }
    } else if (action === ActionType.Updated) {
      if (el instanceof SectionConfiguration) {
        const existingConf = getSectionConfiguration(el.id, configurations)
        const withFields = existingConf ? el.replaceFields(existingConf.fields) : el;
        setConfigurations(list.replace(withFields.id, withFields).configurations);
      } else if (el instanceof FieldConfiguration) {
        setConfigurations(list.replaceField(el).configurations);
      }
    }
    history.push(`${match.url}`);
  };

  const onRulesUpdate = (newRules: ConfigurationRules) => {
    if (newRules.freePaidStatus !== rules.freePaidStatus) {
      setSections(FormConfiguration.setFreePaidStatus(newRules.freePaidStatus, sections, configurations));
    }
    if (newRules.defaultLanguage !== rules.defaultLanguage) {
      setLanguage(newRules.defaultLanguage);
    }
    setRules(newRules);
  };

  const onFieldUpdate = (sectionId: SectionId, fields: IFieldSkeleton[]) =>
    setSections(FormConfiguration.replaceFields(sectionId, fields, sections));

  const onInstructionUpdate = (newInstructions?: string) => setInstructions(newInstructions);

  const onSectionDelete = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    setSections(FormConfiguration.deleteSection(id, sections));
  };

  const onFormSubmit = () => {
    const input = prepareFormInput(sections, rules, editingRights, instructions);
    if (!withInstructions) {
      delete input.instructions;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    delete input.editingRights;
    if (onSubmit) {
      onSubmit(input);
    }
  };

  const reorder = (newState: SectionSkeleton[]) => {
    if (newState.length !== sections.length) {
      const existingIds = sections.map((x) => x.id);
      const newElement = newState.find((x) => existingIds.indexOf(x.id) < 0);
      if (newElement) {
        setTimeout(() => {
          setSections(newState);
          // onSectionAdd(newElement.id);
        }, 100);
      }
    } else {
      const equal = sections.every((value, index) => value.id === newState[index].id);
      if (!equal) {
        setSections(newState);
      }
    }
  };

  const view = useCallback(
    () =>
      FormConfiguration.getView(language, rules.defaultLanguage, sections, configurations)
    , [language, sections, configurations, rules],
  );

  if (editingRights === EditingRights.ReadOnly) {
    return (
      <ReadOnlyForm language={language} setLanguage={setLanguage} withInstructions={withInstructions} view={view}/>);
  }

  return (
    <div>
      <NewSidePanel>
        <Switch>
          <Route exact path={match.path}>
            <SideFormListOfFields sections={view().availableFields}
              selectedSectionIds={view().selectedSections.map((x) => x.id)}
              language={language}
              url={match.url}
              onDelete={deleteElement}
              createOption={editingRights === EditingRights.Full}/>
          </Route>
          <Route path={`${match.path}/section/:id`}>
            <SideSectionForm
              sections={configurations}
              defaultLanguage={rules.defaultLanguage}
              onCreateUpdate={finishElementCreateUpdate}
            />
          </Route>
          <Route path={`${match.path}/section`}>
            <SideSectionForm
              sections={configurations}
              defaultLanguage={rules.defaultLanguage}
              onCreateUpdate={finishElementCreateUpdate}
            />
          </Route>
          <Route path={`${match.path}/field/:id`}>
            <SideFieldForm
              sections={configurations}
              defaultLanguage={rules.defaultLanguage}
              onCreateUpdate={finishElementCreateUpdate}/>
          </Route>
          <Route path={`${match.path}/field`}>
            <SideFieldForm
              sections={configurations}
              defaultLanguage={rules.defaultLanguage}
              onCreateUpdate={finishElementCreateUpdate}/>
          </Route>
        </Switch>
      </NewSidePanel>
      <ReactForm onSubmit={onFormSubmit}>
        <div className="max-w-screen-md mb-5">
          {tip && <>{tip}</>}
          <div className="bg-blue-light px-4 py-5 mb-3 rounded-lg p-6">
            <div className="md:col-span-7 -mt-2.5 -mb-4">
              <ConfigurationRulesBlock rules={rules} updateRules={onRulesUpdate} excludeV1={excludeV1}
                editingRights={editingRights}/>
            </div>
          </div>
          <div className="pt-4">
            <h2 className="text-2xl pb-3 flex justify-between">
              <span className="font-bold">Form Template</span>
              <LanguageFilter setSelectedLanguage={(lang) => setLanguage(lang)}
                selectedLanguage={language}/>
            </h2>
            {withInstructions &&
            <InstructionSection instructions={instructions} onChange={onInstructionUpdate}/>
            }
            <ReactSortable list={sections} setList={reorder}
              group="sections"
              ghostClass="block-sortable-ghost" dragClass="block-sortable-drag"
              animation={250} easing="cubic-bezier(0.45, 0, 0.55, 1)"
              handle=".sort-handle">
              {view().selectedSections.map((section) =>
                <SortableSection section={section} key={`main-${section.id}`} url={match.url}
                  onDelete={onSectionDelete} selectedSections={view().selectedSections}
                  onFieldUpdate={onFieldUpdate}
                  createOption={editingRights === EditingRights.Full}/>)
              }
            </ReactSortable>
          </div>
        </div>
        <div className="pb-28 text-base">
          <div className="grid grid-cols-6 gap-4 items-start">
            {view().translationRequired() &&
            <div className="col-span-4">
              <Tip type={TipType.Error}>
                <div>
                  <p><b>Important:</b> Some sections or {formTypeContext.token}s do not have translations
                                       for the {getLanguage(rules.defaultLanguage)} language. As it is the default
                                       language for this configuration,
                                       you cannot save the changes. Either add translations or delete non-translated
                                       elements. </p>
                  {language !== rules.defaultLanguage &&
                  <p><UILink href="#" onClick={() => setLanguage(rules.defaultLanguage)}>See the elements</UILink></p>
                  }
                </div>
              </Tip>
            </div>
            }
            <div className="col-span-3">
              <PrimaryButton disabled={view().translationRequired()}>Save changes</PrimaryButton>
              <TextButton onClick={(e) => {
                e.preventDefault();
                window.location.assign(backLink)
              }}>Cancel</TextButton>
            </div>
          </div>
        </div>
      </ReactForm>
    </div>
  );
}
